import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useSnackbar } from 'material-ui-snackbar-provider';
import { Box, Tooltip } from '@mui/material';
import { cloneDeep, isFinite } from 'lodash';
import cx from 'classnames';
import CommentOutlinedIcon from '@mui/icons-material/ChatBubbleOutlineRounded';
import StarOutlinedIcon from '@mui/icons-material/StarOutlineRounded';
import SelectedIcon from '@mui/icons-material/CheckBoxOutlined';
import FilterIcon from '@mui/icons-material/FilterAltOutlined';

import { FavouritesPanel } from '../../ClipFavourites/Panel';
import { useClipFavourites } from '../../ClipFavourites/context';
import { useClipFilter } from '../../ClipFilter/context';
import { FilterPanel } from '../../ClipFilter/Panel';
import { useDrawingMarkers } from '../../VideoDrawing/context';
import favouriteStyles from '../../ClipFavourites/Favourites.module.scss';
import {
  CLIP,
  COMMENT,
  TOPIC,
  COMMENT_PARENT_TYPE,
  NOTIFICATION,
  VIDEOFEED_TYPE,
  VIDEOFEED,
  COLLABORATIVE_FOLDER_TYPE,
} from '../../../enums';
import {
  imperativeMarkerSetter,
  triggerRefresh,
  updateMarkerPosition,
  updateSortedSelectedClips,
  updateVideoPlayedSeconds,
} from '../../../store/videos/videoSlice';
import {
  setComposerMarkerPosition,
  updateComposerRows,
  updateCurrentSelectedComposerClips,
  updateCustomTime,
} from '../../../store/composers/composersSlice';
import MinimizeIcon from '../../../assets/icons/minimizeIcon.svg';
import { useAppDispatch, useAppSelector } from '../../../store/hooks';
import {
  deleteComment,
  getClipsCommentsByClipIds,
  getComposerDataByVideoFeedIdForVideoComments,
  getFolderById,
  updateComment,
} from '../../../api';
import { ACCESS_TYPE_ENUM } from '../../../enums/ACCESS_ENUM';
import { RolesLowerCase } from '../../../enums/ROLES';
import { addZeroToNumber } from '../../Timeline/utils';
import { findSearchValue, timeLineFormatTime } from '../../../helpers';
import { getActiveReplies, compareCommentsByTime, compareCommentsByTimestamp, isVisibleComment, CommentDrawingFilter, matchesCommentDrawingFilter } from '../../../helpers/comments';
import styles from './VideoComments.module.scss';
import LinearProgress from '../LinearProgress';
import { TRow } from '../../VideoPage';
import Footer from './CommentFooter';
import Comment from './Comment';
import Modal from '../Modal';
import { ContainedButton, OutlinedButton } from '../Buttons';
import SendClipModal from '../../Timeline/SendClipModal';
import NewComposerModal from '../../Timeline/NewComposerModal';
import { sendCommentToComposer, updateDrawingMarker } from '../../../api/commentsApi';
import { getComposerWebPreferences } from '../../../helpers/localStorage';
import { toFollowPlayhead } from '../../../store/sharedStore/sharedStoreSlice';

enum COMMENT_LOGIC {
  ALL = 'ALL',
  POPPING = 'POPPING',
}

export const SidebarToggle = ({ expanded, onClick }: { expanded: boolean; onClick: () => void }) => (
  <button type="button" className={styles.sidebarToggle} onClick={onClick} aria-expanded={expanded}
    aria-label={expanded ? 'Minimize sidebar' : 'Expand sidebar'} title={expanded ? 'Minimize sidebar' : 'Expand sidebar'}>
    {expanded && <span>Minimize sidebar</span>}
    <img src={MinimizeIcon} alt="" />
  </button>
);

const VideoComments = ({ switchCommentsView }: { switchCommentsView: () => void }) => {
  const { markers: drawingMarkers, showMarker, showVideoComment, refresh: refreshDrawingMarkers, discussionRequest, dismissDiscussion } = useDrawingMarkers();
  const isTimeline = useMemo(() => window.location.href.includes('/Timeline'), [window.location.href]);
  const { sidebarView, setSidebarView, refreshComments } = useClipFilter();
  const { setPlaybackList, setWorkspace } = useClipFavourites();
  useEffect(() => {
    setPlaybackList(sidebarView === 'selected' || sidebarView === 'favourites' ? sidebarView : null);
    return () => setPlaybackList(null);
  }, [sidebarView, setPlaybackList]);
  const [commentList, setCommentList] = useState<COMMENT[] | null>(null);
  const [drawingFilter, setDrawingFilter] = useState<CommentDrawingFilter>('all');
  const videoDrawingMarkers = useMemo(() => drawingMarkers.filter((comment) => comment.clip_id == null && comment.parent_type !== COMMENT_PARENT_TYPE.CLIP), [drawingMarkers]);
  const [comments, setComments] = useState<COMMENT[]>([]);
  const [allTopics, setAllTopics] = useState<TOPIC[]>([]);
  const [timestampTopics, setTimestampTopics] = useState<TOPIC[]>([]);
  const [second, setSecond] = useState<string>('0');
  const [feedType, setFeedType] = useState<VIDEOFEED_TYPE>();
  const [disableComment, setDisableComment] = useState(false);
  const [disableFooter, setDisableFooter] = useState(false);
  const [selectedClipsList, setSelectedClipsList] = useState<CLIP[]>([]);
  const [loading, setLoading] = useState(false);
  const { videoPlayedSeconds, sortedSelectedClips, videoFeed, refreshTrigger, markerPosition, videoLength, videoControls } = useAppSelector(
    (state) => state.videoStore,
  );
  const {
    currentSelectedComposerClips,
    composerPlayControl,
    customTime,
    composers,
    currentComposerData,
    composerRows,
    refreshTrigger: composerRefreshTrigger,
    commentFetchTrigger,
  } = useAppSelector((state) => state.composersStore);
  const videoCommentList = useMemo(() => [
    ...(commentList ?? (!isTimeline ? currentComposerData?.comments : undefined) ?? [])
      .filter((comment) => !comment.drawing && isVisibleComment(comment)),
    ...videoDrawingMarkers,
  ], [isTimeline, commentList, currentComposerData?.comments, videoDrawingMarkers]);
  const feedId = findSearchValue('feedId');
  const discussionClipId = discussionRequest?.comment?.clip_id ?? discussionRequest?.clipId ??
    (discussionRequest?.comment?.parent_type === COMMENT_PARENT_TYPE.CLIP ? discussionRequest.comment.parent_id : undefined);
  const { currentUser } = useAppSelector((state) => state.user);
  const [section, setSection] = useState<COMMENT_PARENT_TYPE>(COMMENT_PARENT_TYPE.CLIP);
  const notification = useSnackbar();
  const dispatch = useAppDispatch();
  const [videoFeedData, setVideoFeedData] = useState<VIDEOFEED>();
  const [commentToSend, setCommentToSend] = useState<COMMENT | null>(null);
  const [showCommentClipRange, setShowCommentClipRange] = useState(false);
  const [commentClipRange, setCommentClipRange] = useState({ inSeconds: '10', outSeconds: '10' });
  const [showCommentComposerPicker, setShowCommentComposerPicker] = useState(false);
  const [showNewCommentComposer, setShowNewCommentComposer] = useState(false);
  const commentSendOperation = useRef<{ key: string; id: string } | null>(null);
  const { currentVideoThatInFrame } = useAppSelector((state) => state.sharedStore);
  const isContentAdminOA = findSearchValue('allContent') === 'true' && currentUser?.role?.title === RolesLowerCase.ORGANIZATION_ADMIN;
  const commentSource = (!isTimeline ? currentComposerData : null) || videoFeedData || videoFeed.find((feed) => String(feed.id) === String(feedId));
  const canCreateCommentClip = !!currentUser && (
    currentUser.role?.title === RolesLowerCase.ORGANIZATION_ADMIN ||
    String(commentSource?.created_by?.id) === String(currentUser.id) ||
    [ACCESS_TYPE_ENUM.COMMENT_AND_CREATE, ACCESS_TYPE_ENUM.COMMENT_CREATE_AND_RESHARE, ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE]
      .includes(commentSource?.highest_user_access?.access?.title as ACCESS_TYPE_ENUM)
  );
  const openCommentClipComposer = (comment: COMMENT) => {
    commentSendOperation.current = null;
    setCommentToSend(comment);
    setCommentClipRange({ inSeconds: '10', outSeconds: '10' });
    setShowCommentClipRange(true);
  };
  const clipInSeconds = Number(commentClipRange.inSeconds);
  const clipOutSeconds = Number(commentClipRange.outSeconds);
  const commentClipRangeValid = !!commentClipRange.inSeconds.trim() && !!commentClipRange.outSeconds.trim() &&
    Number.isFinite(clipInSeconds) && Number.isFinite(clipOutSeconds) &&
    clipInSeconds >= 0 && clipOutSeconds >= 0 && clipInSeconds + clipOutSeconds > 0;
  const sendCommentClip = (destination: { destinationId?: string; title?: string }) => {
    if (!commentToSend) return Promise.reject(new Error('Select a video comment first'));
    const preferences = getComposerWebPreferences();
    const data = {
      ...destination,
      ...(destination.destinationId !== undefined && { destinationId: String(destination.destinationId) }),
      ...(destination.title !== undefined && { title: destination.title.trim() }),
      videoId: commentToSend.drawing?.videoId || (currentVideoThatInFrame?.id ? String(currentVideoThatInFrame.id) : undefined),
      inSeconds: clipInSeconds,
      outSeconds: clipOutSeconds,
      addOnBottom: preferences.sendClipsAddOnBottom,
    };
    const key = JSON.stringify([commentToSend.id, data]);
    if (commentSendOperation.current?.key !== key) {
      commentSendOperation.current = { key, id: crypto.randomUUID() };
    }
    return sendCommentToComposer(String(commentToSend.id), { ...data, clientOperationId: commentSendOperation.current.id });
  };

  const noClips = useMemo(() => (videoFeedData?.topics ?? []).every((t) => (t?.clips?.length ?? 0) === 0), [videoFeedData]);

  const timesValid = useMemo(() => {
    const len = Number(videoLength);
    const inRange = (n: any) => Number.isFinite((n = Number(n))) && n >= 0 && n <= len;
    if (!Number.isFinite(len) || len <= 0) return false;

    return (videoFeedData?.topics ?? []).every((t) => (t?.clips ?? []).every((c) => inRange(c.time_start) || inRange(c.time_end)));
  }, [videoFeedData, videoLength]);
  const disableClipCommentSection = useMemo(() => noClips || !timesValid, [noClips, timesValid]);

  useEffect(() => {
    if (isTimeline && disableClipCommentSection) {
      setSection(COMMENT_PARENT_TYPE.VIDEOFEED);
    }
  }, [disableClipCommentSection, isTimeline]);

  const autoPickSection = useRef(true);
  useEffect(() => {
    autoPickSection.current = true;
  }, [feedId]);

  useEffect(() => {
    if (!isTimeline || !autoPickSection.current) return;
    setSection(disableClipCommentSection ? COMMENT_PARENT_TYPE.VIDEOFEED : COMMENT_PARENT_TYPE.CLIP);
  }, [disableClipCommentSection, isTimeline]);

  const [parentFolder, setParentFolder] = useState<COLLABORATIVE_FOLDER_TYPE | null>();
  const [rootFolder, setRootFolder] = useState<COLLABORATIVE_FOLDER_TYPE | null>();

  const folderId = findSearchValue('folderId');
  const folderTrail = findSearchValue('folderTrail');
  const rootFolderId = folderTrail ? decodeURIComponent(folderTrail).split(',').filter(Boolean)[0] : null;
  const isCollaboratorInFolder = (folder?: COLLABORATIVE_FOLDER_TYPE | null) =>
    !!currentUser &&
    !!folder &&
    ((folder.collaborators || []).some((u) => u.email === currentUser.email) ||
      !!(folder.group_collaborators || []).some((g) =>
        [...(g.analysts || []), ...(g.viewers || [])].some((u) => u.email === currentUser.email),
      ));

  const fetchFolderAccessContext = async () => {
    const [parentRes, rootRes] = await Promise.all([
      getFolderById(folderId!),
      rootFolderId && rootFolderId !== folderId ? getFolderById(rootFolderId) : Promise.resolve(null),
    ]);

    if (parentRes?.data) {
      setParentFolder(parentRes.data);
    }

    if (rootRes?.data) {
      setRootFolder(rootRes.data);
    } else {
      setRootFolder(null);
    }
  };

  useEffect(() => {
    if (folderId) {
      fetchFolderAccessContext();
    }
  }, [folderId, rootFolderId]);

  const currentClipId = useMemo(() => {
    const rowIndex = isTimeline ? markerPosition.rowIndex : composerPlayControl.currentRowIndex;
    const clipIndex = isTimeline ? markerPosition.clipIndex : composerPlayControl.currentClipIndex;
    const clip = selectedClipsList.find((item) => item.rowIndex === rowIndex && item.clipIndex === clipIndex);
    return clip ? Number(clip.id) : undefined;
  }, [selectedClipsList, isTimeline, markerPosition.rowIndex, markerPosition.clipIndex, composerPlayControl.currentRowIndex, composerPlayControl.currentClipIndex]);
  const selectedClipHeader = useMemo(() => {
    if (isTimeline && videoFeedData && section === COMMENT_PARENT_TYPE.CLIP && sortedSelectedClips.length) {
      let clip = selectedClipsList.find((c) => c?.clipIndex === markerPosition.clipIndex && c?.rowIndex === markerPosition.rowIndex);

      if (clip && !clip?.topicName) {
        clip = {
          ...clip,
          topicName: videoFeedData.topics[isTimeline ? markerPosition.rowIndex : composerPlayControl.currentRowIndex]?.name ?? 'Topic',
        };
      }

      if (clip && typeof clip?.clipIndex === 'number' && typeof clip?.rowIndex === 'number' && clip?.topicName) {
        return (
          <div className={styles.clipHeader} style={{ marginTop: 0, marginBottom: 0 }}>
            <span className={styles.clipHeaderIndex}>{addZeroToNumber(clip.rowIndex + 1)}</span>
            <span>
              {clip.topicName}, clip {clip.clipIndex + 1}
            </span>
          </div>
        );
      }
    } else if (!isTimeline && videoFeedData && section === COMMENT_PARENT_TYPE.CLIP) {
      let clip = isTimeline
        ? selectedClipsList.find((c) => c?.clipIndex === markerPosition.clipIndex && c?.rowIndex === markerPosition.rowIndex)
        : selectedClipsList.find(
            (c) => c?.clipIndex === composerPlayControl.currentClipIndex && c?.rowIndex === composerPlayControl.currentRowIndex,
          );

      if (clip && !clip?.topicName) {
        clip = {
          ...clip,
          topicName: videoFeedData.topics[isTimeline ? markerPosition.rowIndex : composerPlayControl.currentRowIndex]?.name ?? 'Topic',
        };
      }

      if (clip && typeof clip?.clipIndex === 'number' && typeof clip?.rowIndex === 'number' && clip?.topicName) {
        return (
          <div className={styles.clipHeader} style={{ marginTop: 0, marginBottom: 0 }}>
            <span className={styles.clipHeaderIndex}>{addZeroToNumber(clip.rowIndex + 1)}</span>
            <span>
              {clip.topicName}, clip {clip.clipIndex + 1}
            </span>
          </div>
        );
      }
    }
  }, [
    isTimeline,
    selectedClipsList,
    markerPosition,
    composerPlayControl.currentClipIndex,
    composerPlayControl.currentRowIndex,
    videoFeedData,
    refreshTrigger,
    composerRefreshTrigger,
    composerRows,
    section,
  ]);

  const selectedClipObject = useMemo(() => {
    if (isTimeline && videoFeedData && section === COMMENT_PARENT_TYPE.CLIP && sortedSelectedClips.length) {
      let clip = selectedClipsList.find((c) => c?.clipIndex === markerPosition.clipIndex && c?.rowIndex === markerPosition.rowIndex);

      if (clip && !clip?.topicName) {
        clip = {
          ...clip,
          topicName: videoFeedData.topics[isTimeline ? markerPosition.rowIndex : composerPlayControl.currentRowIndex]?.name ?? 'Topic',
        };
      }

      if (clip && typeof clip?.clipIndex === 'number' && typeof clip?.rowIndex === 'number' && clip?.topicName) {
        return clip;
      }
    } else if (!isTimeline && videoFeedData) {
      let clip = isTimeline
        ? selectedClipsList.find((c) => c?.clipIndex === markerPosition.clipIndex && c?.rowIndex === markerPosition.rowIndex)
        : selectedClipsList.find(
            (c) => c?.clipIndex === composerPlayControl.currentClipIndex && c?.rowIndex === composerPlayControl.currentRowIndex,
          );

      if (clip && !clip?.topicName) {
        clip = {
          ...clip,
          topicName: videoFeedData.topics[isTimeline ? markerPosition.rowIndex : composerPlayControl.currentRowIndex]?.name ?? 'Topic',
        };
      }

      if (clip && typeof clip?.clipIndex === 'number' && typeof clip?.rowIndex === 'number' && clip?.topicName) {
        return clip;
      }
    }
  }, [
    isTimeline,
    selectedClipsList,
    markerPosition,
    composerPlayControl.currentClipIndex,
    composerPlayControl.currentRowIndex,
    videoFeedData,
    refreshTrigger,
    composerRefreshTrigger,
    composerRows,
    section,
  ]);

  const fetchVideofeed = async (id: string) => {
    try {
      setLoading(true);

      const { data } = await getComposerDataByVideoFeedIdForVideoComments(id);
      setVideoFeedData(data);

      const customTopics = cloneDeep(data.topics as TRow[]);
      customTopics.forEach((row, rowIndex) => {
        row.clips.forEach((clip, clipIndex) => {
          customTopics[rowIndex].clips[clipIndex].topicId = row.id;

          if (clip.time_start <= 0 && clip.time_end <= videoLength) {
            customTopics[rowIndex].clips[clipIndex].time_start = 0.1;
          } else if (clip.time_start <= 0 && clip.time_end > videoLength) {
            customTopics[rowIndex].clips[clipIndex].time_start = 0.1;
            customTopics[rowIndex].clips[clipIndex].time_end = videoLength;
          } else if (clip.time_start >= 0 && clip.time_start < videoLength && clip.time_end > videoLength) {
            customTopics[rowIndex].clips[clipIndex].time_end = videoLength;
          }
        });
      });

      customTopics.forEach((row, rowIndex) => {
        customTopics[rowIndex].clips = row.clips.filter((clip) => clip.time_end >= 0);
      });

      setAllTopics(customTopics);
      setCommentList([...data.comments]);
      updateList(data.comments);

      if (currentSelectedComposerClips?.length && !isTimeline) {
        const newComposerRows = composerRows.map((localRow, rowIndex) => {
          const srvTopic = data.topics[rowIndex];

          const newClips = localRow.clips.map((localClip, clipIndex) => {
            const srvClip = srvTopic.clips.find((c: CLIP) => c.id === localClip.id);
            return {
              ...localClip,
              ...srvClip,
              clipIndex,
              rowIndex,
            };
          });

          return {
            ...localRow,
            ...srvTopic,
            clips: newClips,
          };
        });

        dispatch(updateComposerRows(newComposerRows));

        const newCurrentSelectedComposerClips = currentSelectedComposerClips.map((obj) => {
          const srvTopic = data.topics[obj.rowIndex!];
          const srvClip = srvTopic?.clips?.find((_c: CLIP, i: number) => i === obj.clipIndex);
          return srvClip ? { ...obj, comments: srvClip.comments } : obj;
        });

        dispatch(updateCurrentSelectedComposerClips(newCurrentSelectedComposerClips));
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const fetchDataHelper = (id: string) => {
    if (isTimeline || section === COMMENT_PARENT_TYPE.VIDEOFEED) {
      fetchVideofeed(id);
    } else {
      fetchCommentsForComposer();
    }
  };

  const onCommentMutation = (id: string) => {
    refreshComments();
    refreshDrawingMarkers();
    fetchDataHelper(id);
  };

  const updateList = (comment: COMMENT[]) => {
    if (comment) {
      const commentsByTimestamp = comment?.filter((x) => +x.timestamp?.toFixed() <= +second);
      setComments(commentsByTimestamp);
    }
  };

  useEffect(() => {
    setCommentList(null);
    if (feedId) {
      fetchDataHelper(feedId);
    }
  }, [feedId]);

  useEffect(() => {
    const searchfeedTypeValue = findSearchValue('pageType');
    if (searchfeedTypeValue?.includes(VIDEOFEED_TYPE.TIMELINE)) {
      setFeedType(VIDEOFEED_TYPE.TIMELINE);
    } else if (searchfeedTypeValue?.includes(VIDEOFEED_TYPE.MY_COMPOSER)) {
      setFeedType(VIDEOFEED_TYPE.MY_COMPOSER);
    } else if (searchfeedTypeValue?.includes(VIDEOFEED_TYPE.COMPOSER)) {
      setFeedType(VIDEOFEED_TYPE.COMPOSER);
    }
  }, [feedId, refreshTrigger, composerRefreshTrigger]);

  useEffect(() => {
    if (commentFetchTrigger && feedId) {
      fetchDataHelper(feedId);
    }
  }, [commentFetchTrigger]);

  useEffect(() => {
    if (isTimeline) {
      setSecond(videoPlayedSeconds?.toFixed());
    } else {
      setSecond(composerPlayControl.composerControls.playedSeconds?.toFixed());
    }
  }, [videoPlayedSeconds, composerPlayControl.composerControls.playedSeconds, selectedClipsList[0]?.id]);

  const findSelectedClip = () => {
    if (isTimeline) {
      const clip: any = isTimeline
        ? sortedSelectedClips.find((c) => c.rowIndex === markerPosition.rowIndex && markerPosition.clipIndex === c.clipIndex)
        : '';

      if (clip) {
        const clipInd = clip!.clipIndex;
        const topic = allTopics.find((t) => t.id == clip.topicId);

        if (topic) {
          const obj = {
            ...topic,
            clips: topic.clips.filter((c, i) => i === clipInd),
          };
          return obj;
        }
      }
    }
    const rowInd = composerPlayControl.currentRowIndex;
    const clipInd = composerPlayControl.currentClipIndex;
    const topic = allTopics.find((t, i) => i === rowInd);

    if (topic) {
      const obj = {
        ...topic,
        clips: topic.clips.filter((c, i) => i === clipInd),
      };
      return obj;
    }
  };

  const [commentLogic, setCommentLogic] = useState(COMMENT_LOGIC.POPPING);
  useEffect(() => {
    if (section === COMMENT_PARENT_TYPE.VIDEOFEED) {
      const visible = videoCommentList.filter((comment) => comment.parent_type !== 'clip' && matchesCommentDrawingFilter(comment, drawingFilter) &&
        (commentLogic === COMMENT_LOGIC.ALL || +comment.timestamp?.toFixed() <= +second));
      setComments(visible);
    } else if (allTopics?.length) {
      if (commentLogic === COMMENT_LOGIC.POPPING) {
        const selectedC = findSelectedClip();
        setTimestampTopics(selectedC ? [selectedC] : []);
      } else {
        setTimestampTopics(allTopics);
        if (isTimeline && sortedSelectedClips.length === 0) {
          const allClipsParsed: CLIP[] = [];
          allTopics.forEach((t, rowIndex) => {
            const clips = [...JSON.parse(JSON.stringify(t)).clips].map((cl) => ({ ...cl, rowIndex, topicName: t.name }));
            allClipsParsed.push(...clips);
          });
          setSelectedClipsList(allClipsParsed);
        }
      }
    }
  }, [second, videoCommentList, section, allTopics, sortedSelectedClips, markerPosition, composerPlayControl.currentClipIndex, composerPlayControl.currentRowIndex, commentLogic, isTimeline, drawingFilter]);

  useEffect(() => {
    if (window.location.pathname.toLowerCase().includes('composer')) {
      setSelectedClipsList(currentSelectedComposerClips);
      if (autoPickSection.current) setSection(COMMENT_PARENT_TYPE.CLIP);
    } else {
      if (sortedSelectedClips.length === 0) {
        const topicsByTimestamp = allTopics;
        setTimestampTopics(topicsByTimestamp);
        if (isTimeline && sortedSelectedClips.length === 0) {
          const allClipsParsed: CLIP[] = [];

          allTopics.forEach((t, rowIndex) => {
            const clips = [...JSON.parse(JSON.stringify(t)).clips].map((cl) => ({ ...cl, rowIndex, topicName: t.name }));
            allClipsParsed.push(...clips);
          });
          setSelectedClipsList(allClipsParsed);
        }
      } else {
        setSelectedClipsList(sortedSelectedClips);
        setSection(COMMENT_PARENT_TYPE.CLIP);
      }

      sortedSelectedClips.length === 0 && setCommentLogic(COMMENT_LOGIC.ALL);
    }
  }, [sortedSelectedClips, currentSelectedComposerClips, window.location.pathname]);

  const urlParams = document.location.search;
  const commentIdFromQuery = findSearchValue('commentId');
  const replyIdFromQuery = findSearchValue('replyId');
  const commentTabFromQuery = findSearchValue('commentTab');
  const [autoExpandCommentId, setAutoExpandCommentId] = useState<string | null>(null);
  const [autoExpandReplyId, setAutoExpandReplyId] = useState<string | null>(null);
  useEffect(() => {
    if (feedId) {
      const feedToCheck = videoFeed.find((f) => f.id === feedId);
      const composerCheck = composers.find((c) => c.id === feedId);
      const currentComposer = !isTimeline && currentComposerData?.id === feedId ? currentComposerData : null;
      const currentFeedData = videoFeedData?.id === feedId ? videoFeedData : null;
      const activeContent = currentComposer || currentFeedData || feedToCheck || composerCheck;
      const effectiveAccessTitle = currentComposer
        ? currentComposer.highest_user_access?.access?.title
        : currentFeedData?.highest_user_access?.access?.title ||
        feedToCheck?.highest_user_access?.access?.title ||
        composerCheck?.highest_user_access?.access?.title;

      if (
        effectiveAccessTitle === ACCESS_TYPE_ENUM.VIEW_AND_COMMENT ||
        effectiveAccessTitle === ACCESS_TYPE_ENUM.EDIT_AND_RE_SHARE ||
        effectiveAccessTitle === ACCESS_TYPE_ENUM.COMMENT_AND_CREATE ||
        effectiveAccessTitle === ACCESS_TYPE_ENUM.COMMENT_CREATE_AND_RESHARE
      ) {
        setDisableComment(false);
      } else if (
        effectiveAccessTitle === ACCESS_TYPE_ENUM.VIEW ||
        effectiveAccessTitle === ACCESS_TYPE_ENUM.NO_ACCESS
      ) {
        setDisableComment(true);
      } else if (activeContent && activeContent?.created_by?.email != currentUser?.email) {
        setDisableComment(true);
      } else {
        setDisableComment(false);
      }

      if (isCollaboratorInFolder(parentFolder) || isCollaboratorInFolder(rootFolder)) {
        setDisableComment(false);
      }

      if (isContentAdminOA) {
        setDisableComment(false);
      }

      if (isTimeline && section === COMMENT_PARENT_TYPE.CLIP) {
        if (!selectedClipHeader) {
          setDisableFooter(true);
        } else {
          setDisableFooter(false);
        }
      } else {
        setDisableFooter(false);
      }
    }
  }, [
    urlParams,
    feedId,
    videoFeed,
    composers,
    currentComposerData,
    currentUser,
    parentFolder,
    rootFolder,
    selectedClipsList,
    isTimeline,
    section,
    selectedClipHeader,
    videoFeedData,
    isContentAdminOA,
  ]);

  useEffect(() => {
    if (isTimeline) {
    } else {
      dispatch(updateSortedSelectedClips([]));
    }
  }, [feedType]);

  const handleDelete = async (comment: COMMENT) => {
    if (!feedId) return;
    if (getActiveReplies(comment).length && !window.confirm('Delete this comment? Replies will remain under “Comment deleted”.')) return;
    try {
      setLoading(true);
      await deleteComment(comment.id, feedId);
      refreshComments();
      refreshDrawingMarkers();
      return await fetchVideofeed(feedId as string);
    } catch (error) {
      notification.showMessage(NOTIFICATION.NOT_DELETED);
    } finally {
      setLoading(false);
    }
  };

  const handleEditSubmit = async (comment: COMMENT, newMessage: string) => {
    if (!feedId) return;
    try {
      setLoading(true);
      if (comment.drawing) await updateDrawingMarker(comment.id, { message: newMessage });
      else await updateComment(comment.id, { newMessage });
      refreshComments();
      refreshDrawingMarkers();
      notification.showMessage(NOTIFICATION.SUCCESSFUL_MESSAGE_CHANGE);
      return await fetchVideofeed(feedId as string);
    } catch (error) {
      notification.showMessage(NOTIFICATION.NOT_EDITED);
    } finally {
      setLoading(false);
    }
  };

  const setCommentSection = (commentSection: COMMENT_PARENT_TYPE) => {
    autoPickSection.current = false;
    setSection(commentSection);
  };

  useEffect(() => {
    if (!discussionRequest) return;
    const comment = discussionRequest.comment;
    setCommentSection(discussionRequest.clipId != null || comment?.clip_id != null || comment?.parent_type === COMMENT_PARENT_TYPE.CLIP
      ? COMMENT_PARENT_TYPE.CLIP : COMMENT_PARENT_TYPE.VIDEOFEED);
    setCommentLogic(COMMENT_LOGIC.ALL); setDrawingFilter('all');
    setAutoExpandCommentId(comment ? String(comment.id) : null);
  }, [discussionRequest]);

  useEffect(() => {
    if (!commentIdFromQuery) {
      setAutoExpandCommentId(null);
      setAutoExpandReplyId(null);
      return;
    }

    setAutoExpandCommentId(commentIdFromQuery);
    setAutoExpandReplyId(replyIdFromQuery || null);

    if (isTimeline) {
      const commentInFeed = videoCommentList.find((comment) => String(comment.id) === String(commentIdFromQuery));
      let commentInClip = false;
      if (!commentInFeed && allTopics?.length) {
        commentInClip = allTopics.some((topic) =>
          topic?.clips?.some((clip) => clip?.comments?.some((comment) => String(comment.id) === String(commentIdFromQuery))),
        );
      }
      setCommentLogic(COMMENT_LOGIC.ALL);
      if (commentTabFromQuery === 'video' || commentInFeed) {
        setCommentSection(COMMENT_PARENT_TYPE.VIDEOFEED);
      } else if (commentInClip) {
        setCommentSection(COMMENT_PARENT_TYPE.CLIP);
      }
    } else {
      setCommentLogic(COMMENT_LOGIC.ALL);
    }
  }, [commentIdFromQuery, replyIdFromQuery, commentTabFromQuery, videoCommentList, allTopics, isTimeline]);

  const [prevClipId, setPrevClipId] = useState('');
  const onClipHeaderSelect = (clipId: string) => {
    dismissDiscussion();
    setAutoExpandCommentId(null);
    const clip = selectedClipsList.find((c) => c.id === clipId);
    if (clip) {
      if (isTimeline) {
        if (prevClipId === clipId) {
          if (sortedSelectedClips.length > 1) {
            const newArr = sortedSelectedClips.filter((c) => +c.id !== +clipId);
            dispatch(updateSortedSelectedClips(newArr));
          } else {
            dispatch(updateSortedSelectedClips([]));
          }
          dispatch(toFollowPlayhead(true));
          dispatch(triggerRefresh());
          setPrevClipId('');
          return;
        }
        // @ts-ignore
        dispatch(updateSortedSelectedClips([clip]));
        dispatch(
          updateMarkerPosition({
            rowIndex: clip.rowIndex,
            clipIndex: clip.clipIndex,
          }),
        );
        dispatch(updateVideoPlayedSeconds(clip.time_start));
        dispatch(
          imperativeMarkerSetter({
            rowIndex: clip.rowIndex!,
            clipIndex: clip.clipIndex!,
            seconds: clip.time_start,
          }),
        );
        dispatch(toFollowPlayhead(true));
        dispatch(triggerRefresh());
      } else {
        dispatch(
          setComposerMarkerPosition({
            rowIndex: clip.rowIndex,
            clipIndex: clip.clipIndex,
          }),
        );
        dispatch(updateCustomTime(clip.time_start));
        dispatch(toFollowPlayhead(true));
        dispatch(triggerRefresh());
      }
    }
    setPrevClipId(clipId);
    dispatch(toFollowPlayhead(true));
  };

  const [composerTopics, setComposerTopics] = useState<any[]>([]);
  const composerCommentsRequest = useRef(0);
  const fetchCommentsForComposer = async () => {
    const requestId = ++composerCommentsRequest.current;
    setComposerTopics([]);
    try {
      setLoading(true);
      let clipIds: number[] = currentSelectedComposerClips.map((c) => +c.id);
      if (commentLogic === COMMENT_LOGIC.POPPING && currentClipId) {
        clipIds = [currentClipId];
      }
      if (commentLogic === COMMENT_LOGIC.ALL && discussionClipId != null && !clipIds.includes(Number(discussionClipId))) clipIds.push(Number(discussionClipId));
      const res = await getClipsCommentsByClipIds(clipIds);

      const topics: any[] = (res.data as any[]).reduce((acc: any[], comment: any) => {
        const { clip: clipRaw } = comment;
        const { topic, ...clipData } = clipRaw;
        const topicId = topic.id;
        const topicName = topic.name;

        let topicGroup = acc.find((t) => t.topicId === topicId);
        if (!topicGroup) {
          topicGroup = { topicId, topicName, clips: [] as any[] };
          acc.push(topicGroup);
        }

        let clipGroup = topicGroup.clips.find((c: any) => c.id === clipData.id);
        if (!clipGroup) {
          clipGroup = { ...clipData, comments: [] as any[] };
          topicGroup.clips.push(clipGroup);
        }

        clipGroup.comments.push(comment);
        return acc;
      }, [] as any[]);

      if (requestId === composerCommentsRequest.current) setComposerTopics(topics);
    } catch (err: any) {
      console.log(err);
    } finally {
      if (requestId === composerCommentsRequest.current) setLoading(false);
    }
  };

  useEffect(() => {
    if (feedId && !isTimeline && (currentSelectedComposerClips.length || discussionRequest)) {
      fetchCommentsForComposer();
    }
  }, [feedId, isTimeline, commentLogic, currentClipId, currentSelectedComposerClips, discussionRequest]);

  return (
    <div className={cx(styles.comments, sidebarView === 'comments' && styles.commentsMode)} onPointerDownCapture={() => setWorkspace('sidebar')}>
      <div className={styles.commentsHeader}>
        <div className={styles.heading}>
          <div className={cx(favouriteStyles.tabs, favouriteStyles.compactTabs)} role="group" aria-label="Sidebar controls">
            <div className={favouriteStyles.tabList} role="tablist" aria-label="Sidebar view">
              <Tooltip title="Comments"><button className={favouriteStyles.tab} type="button" role="tab" aria-label="Comments" aria-selected={sidebarView === 'comments'} onClick={() => setSidebarView('comments')}>
                <CommentOutlinedIcon /> <span className={favouriteStyles.tabLabel}>Comments</span>
              </button></Tooltip>
              <Tooltip title="Selected clips"><button className={favouriteStyles.tab} type="button" role="tab" aria-label="Selected clips" aria-selected={sidebarView === 'selected'} onClick={() => setSidebarView('selected')}>
                <SelectedIcon /> <span className={favouriteStyles.tabLabel}>Selected</span>
              </button></Tooltip>
              <Tooltip title="Favourites"><button className={favouriteStyles.tab} type="button" role="tab" aria-label="Favourites" aria-selected={sidebarView === 'favourites'} onClick={() => setSidebarView('favourites')}>
                <StarOutlinedIcon /> <span className={favouriteStyles.tabLabel}>Favourites</span>
              </button></Tooltip>
              <Tooltip title="Filter clips"><button className={favouriteStyles.tab} type="button" role="tab" aria-label="Filter" aria-selected={sidebarView === 'filter'} onClick={() => setSidebarView('filter')}>
                <FilterIcon /> <span className={favouriteStyles.tabLabel}>Filter</span>
              </button></Tooltip>
            </div>
          </div>
        </div>
      </div>
      {sidebarView === 'filter' ? <FilterPanel /> : sidebarView !== 'comments' ? (
        <FavouritesPanel
          key={sidebarView}
          disableComments={disableComment}
          onCommentsChange={() => onCommentMutation(feedId!)}
          selectedClips={sidebarView === 'selected' ? (isTimeline ? sortedSelectedClips : currentSelectedComposerClips) : undefined}
          playhead={isTimeline ? {
            ...markerPosition,
            seconds: videoControls.playing ? videoControls.playedSeconds : videoPlayedSeconds,
            playing: videoControls.playing,
          } : {
            rowIndex: composerPlayControl.currentRowIndex,
            clipIndex: composerPlayControl.currentClipIndex,
            seconds: composerPlayControl.composerControls.playedSeconds > 0
              ? composerPlayControl.composerControls.playedSeconds : customTime,
            playing: composerPlayControl.composerControls.playing,
          }}
        />
      ) : <>
        <div className={styles.commentsToggle}>
              <div
                className={cx(
                  section === COMMENT_PARENT_TYPE.VIDEOFEED ? styles.activeCommentButton : '',
                  styles.commentButton,
                )}
                onClick={() => {
                  if (!isTimeline || !sortedSelectedClips?.length || drawingMarkers.length) {
                    setCommentSection(COMMENT_PARENT_TYPE.VIDEOFEED);
                  }
                }}
              >
                Video comments
              </div>

              {!isTimeline || !disableClipCommentSection ? (
                <div
                  className={cx(
                    section === COMMENT_PARENT_TYPE.CLIP ? styles.activeCommentButton : '',
                    styles.commentButton,
                  )}
                  onClick={() => {
                    setCommentSection(COMMENT_PARENT_TYPE.CLIP);
                    sortedSelectedClips.length === 0 && setCommentLogic(COMMENT_LOGIC.ALL);
                  }}
                >
                  Clip comments
                </div>
              ) : (
                <div className={styles.commentButton} style={{ cursor: 'not-allowed' }}>
                  Clip comments
                </div>
              )}
        </div>

      {section === COMMENT_PARENT_TYPE.VIDEOFEED && (
        <div className={styles.commentLogic}>
          <div
            onClick={() => setCommentLogic(COMMENT_LOGIC.ALL)}
            className={cx(styles.logicBtn, commentLogic === COMMENT_LOGIC.ALL ? styles.logicBtnActive : '')}
          >
            All Comments
          </div>
          <div
            onClick={() => setCommentLogic(COMMENT_LOGIC.POPPING)}
            className={cx(styles.logicBtn, commentLogic === COMMENT_LOGIC.POPPING ? styles.logicBtnActive : '')}
          >
            Time-synced Comments
          </div>
        </div>
      )}

      {section === COMMENT_PARENT_TYPE.CLIP && (!isTimeline || sortedSelectedClips.length > 0) && (
        <div className={styles.commentFilters}>
          <div className={styles.filterLabel}>Show comments from</div>
          <div className={styles.commentLogic} role="group" aria-label="Comment clip scope">
            <button type="button" onClick={() => setCommentLogic(COMMENT_LOGIC.ALL)}
              aria-pressed={commentLogic === COMMENT_LOGIC.ALL}
              className={cx(styles.logicBtn, commentLogic === COMMENT_LOGIC.ALL && styles.logicBtnActive)}>
              Selected clips{(isTimeline ? sortedSelectedClips : currentSelectedComposerClips).length > 0 &&
                ` (${(isTimeline ? sortedSelectedClips : currentSelectedComposerClips).length})`}
            </button>
            <button type="button" onClick={() => setCommentLogic(COMMENT_LOGIC.POPPING)}
              aria-pressed={commentLogic === COMMENT_LOGIC.POPPING}
              className={cx(styles.logicBtn, commentLogic === COMMENT_LOGIC.POPPING && styles.logicBtnActive)}>
              Now playing
            </button>
          </div>
        </div>
      )}

      <div className={styles.commentFilters}>
        <div className={styles.filterLabel}>Drawings</div>
        <div className={styles.commentLogic} role="group" aria-label="Filter comments by drawings">
          {([
            ['all', 'All', 'All comments'],
            ['with', 'With', 'Comments with drawings only'],
            ['without', 'Without', 'Comments without drawings only'],
          ] as const).map(([value, label, title]) => (
            <button key={value} type="button" aria-label={title} title={title}
              aria-pressed={drawingFilter === value} onClick={() => setDrawingFilter(value)}
              className={cx(styles.logicBtn, drawingFilter === value && styles.logicBtnActive)}>
              {label}
            </button>
          ))}
        </div>
      </div>

      {isTimeline && (
        <div className={styles.commentsWrapper}>
          {section === COMMENT_PARENT_TYPE.CLIP ? (
            <div className={cx(styles.commentsList, styles.lastChildHelperClass)}>
              {timestampTopics.map((topic) => {
                return (
                  <div key={topic.id}>
                    {topic.clips
                      ?.filter((clip) =>
                        commentLogic === COMMENT_LOGIC.ALL
                          ? true
                          : +second >= Math.round(clip.time_start) && +second <= Math.round(clip.time_end),
                      )
                      .map(
                        (clip) =>
                          clip.comments.some((comment) => matchesCommentDrawingFilter(comment, drawingFilter)) &&
                          (selectedClipsList.find((x) => x?.id === clip?.id) ||
                            String(clip.id) === String(discussionRequest?.comment?.clip_id ?? discussionRequest?.clipId ??
                              (discussionRequest?.comment?.parent_type === COMMENT_PARENT_TYPE.CLIP ? discussionRequest.comment?.parent_id : undefined))) && (
                            <div key={`${clip.id}_clip`}>
                              <>
                                <div className={cx(styles.clipHeader, styles.clipHeaderEffect)} onClick={() => onClipHeaderSelect(clip.id)}>
                                  <span className={styles.clipHeaderIndex}>
                                    {addZeroToNumber(allTopics.findIndex((t) => t.id === topic.id) + 1)}
                                  </span>
                                  <span>
                                    {topic.name}, clip {/* @ts-ignore */}
                                    {clip?.clipIndex + 1}
                                  </span>
                                </div>
                                {clip.comments
                                  .filter((comment) => matchesCommentDrawingFilter(comment, drawingFilter))
                                  .sort(compareCommentsByTime)
                                  .map((comment) => (
                                    <div key={comment.id}>
                                      <Comment
                                        onCreateClip={canCreateCommentClip ? openCommentClipComposer : undefined}
                                        videofeedId={feedId!}
                                        fetch={onCommentMutation}
                                        comment={comment}
                                        onEditSubmit={handleEditSubmit}
                                        onDelete={handleDelete}
                                        disable={disableComment}
                                        autoEdit={String(comment.id) === String(discussionRequest?.comment?.id) && discussionRequest?.edit}
                                        revealSequence={String(comment.id) === String(discussionRequest?.comment?.id) ? discussionRequest?.sequence : undefined}
                                        autoExpand={(!!autoExpandReplyId && String(comment.id) === String(autoExpandCommentId)) || String(comment.id) === String(discussionRequest?.comment?.id)}
                                      />
                                    </div>
                                  ))}
                              </>
                            </div>
                          ),
                      )}
                  </div>
                );
              })}
            </div>
          ) : (
            comments
              .filter((comment) => isVisibleComment(comment))
              .sort(compareCommentsByTimestamp)
              .map((comment) => (
                <div key={comment.id} className={styles.lastChildHelperClass}>
                  {isFinite(comment?.timestamp) && (
                    <div className={cx(styles.clipHeader, styles.clipHeaderEffect)} onClick={() => comment.drawing ? showMarker(comment) : showVideoComment(comment)}>
                      <span className={styles.clipHeaderIndex}>{timeLineFormatTime(comment.timestamp)}</span>
                    </div>
                  )}{' '}
                  <Comment
                    onCreateClip={canCreateCommentClip ? openCommentClipComposer : undefined}
                    videofeedId={feedId!}
                    fetch={onCommentMutation}
                    onEditSubmit={handleEditSubmit}
                    comment={comment}
                    onDelete={handleDelete}
                    disable={disableComment}
                    autoEdit={String(comment.id) === String(discussionRequest?.comment?.id) && discussionRequest?.edit}
                    revealSequence={String(comment.id) === String(discussionRequest?.comment?.id) ? discussionRequest?.sequence : undefined}
                    autoExpand={(!!autoExpandReplyId && String(comment.id) === String(autoExpandCommentId)) || String(comment.id) === String(discussionRequest?.comment?.id)}
                  />
                </div>
              ))
          )}
        </div>
      )}

      {!isTimeline && (
        <div className={styles.commentsWrapper}>
          {section === COMMENT_PARENT_TYPE.VIDEOFEED ? videoCommentList
            .filter((comment) => matchesCommentDrawingFilter(comment, drawingFilter) && (commentLogic === COMMENT_LOGIC.ALL || comment.timestamp <= +second))
            .sort(compareCommentsByTimestamp)
            .map((comment) => <div key={comment.id} className={styles.lastChildHelperClass}>
              <Comment onCreateClip={canCreateCommentClip ? openCommentClipComposer : undefined} videofeedId={feedId!} fetch={onCommentMutation} comment={comment} onEditSubmit={handleEditSubmit}
                onDelete={handleDelete} disable={disableComment}
                autoEdit={String(comment.id) === String(discussionRequest?.comment?.id) && discussionRequest?.edit}
                revealSequence={String(comment.id) === String(discussionRequest?.comment?.id) ? discussionRequest?.sequence : undefined}
                autoExpand={(!!autoExpandReplyId && String(comment.id) === String(autoExpandCommentId)) || String(comment.id) === String(discussionRequest?.comment?.id)} />
            </div>) :
          <div className={styles.commentsList}>
            {composerTopics.map((topic) => {
              return (
                <div key={topic.topicId}>
                  {topic?.clips?.map(
                    (clip: any) =>
                      clip.comments.some((comment: COMMENT) => matchesCommentDrawingFilter(comment, drawingFilter)) && (
                        <div key={`${clip.id}_clip`} className={styles.lastChildHelperClass}>
                          <>
                            <div className={cx(styles.clipHeader, styles.clipHeaderEffect)} onClick={() => onClipHeaderSelect(clip.id)}>
                              <span className={styles.clipHeaderIndex}>
                                {addZeroToNumber(allTopics.findIndex((t) => t.id === topic.id) + 1)}
                              </span>
                              <span>
                                {topic.topicName}, clip {clip?.clipIndex + 1}
                              </span>
                            </div>
                            {clip.comments
                              .filter((comment: COMMENT) => matchesCommentDrawingFilter(comment, drawingFilter))
                              .sort(compareCommentsByTime)
                              .map(
                                (comment: any) =>
                                  comment?.id && (
                                    <div key={comment.id} className={styles.lastChildHelperClass} style={{ marginBottom: '12px' }}>
                                      <Comment
                                        onCreateClip={canCreateCommentClip ? openCommentClipComposer : undefined}
                                        videofeedId={feedId!}
                                        fetch={onCommentMutation}
                                        comment={comment}
                                        onEditSubmit={handleEditSubmit}
                                        onDelete={handleDelete}
                                        disable={disableComment}
                                        autoEdit={String(comment.id) === String(discussionRequest?.comment?.id) && discussionRequest?.edit}
                                        revealSequence={String(comment.id) === String(discussionRequest?.comment?.id) ? discussionRequest?.sequence : undefined}
                                        autoExpand={(!!autoExpandReplyId && String(comment.id) === String(autoExpandCommentId)) || String(comment.id) === String(discussionRequest?.comment?.id)}
                                      />
                                    </div>
                                  ),
                              )}
                          </>
                        </div>
                      ),
                  )}
                </div>
              );
            })}
          </div>}
        </div>
      )}

      {(isTimeline || section === COMMENT_PARENT_TYPE.CLIP) && <div className={styles.footerWrapper}>
        {selectedClipHeader}
        {loading && (
          <>
            <Box sx={{ width: '100%' }}>
              <LinearProgress />
            </Box>
          </>
        )}
        <Footer
          videofeedId={feedId!}
          selectedClipsList={selectedClipsList}
          videoPlayedSeconds={videoPlayedSeconds}
          fetch={onCommentMutation}
          disable={disableComment || disableFooter}
          section={section}
          selectedClipObject={selectedClipObject}
          clipId={section === COMMENT_PARENT_TYPE.CLIP && discussionClipId != null ? String(discussionClipId) : undefined}
        />
      </div>}
      </>}
      <SidebarToggle expanded onClick={switchCommentsView} />
      {showCommentClipRange && <Modal open onClose={() => setShowCommentClipRange(false)}>
        <form className={styles.commentClipRange} onSubmit={(event) => {
          event.preventDefault();
          if (!commentClipRangeValid) return;
          setShowCommentClipRange(false);
          setShowCommentComposerPicker(true);
        }}>
          <h3>Create clip from comment</h3>
          <p>Comment at {timeLineFormatTime(commentToSend!.timestamp)}</p>
          <div className={styles.commentClipRangeFields}>
            <label>Seconds before comment
              <input type="number" min="0" step="any" required autoFocus value={commentClipRange.inSeconds}
                onChange={(event) => setCommentClipRange({ ...commentClipRange, inSeconds: event.target.value })} />
            </label>
            <label>Seconds after comment
              <input type="number" min="0" step="any" required value={commentClipRange.outSeconds}
                onChange={(event) => setCommentClipRange({ ...commentClipRange, outSeconds: event.target.value })} />
            </label>
          </div>
          <div className={styles.commentClipRangeActions}>
            <OutlinedButton onClick={() => setShowCommentClipRange(false)}>Cancel</OutlinedButton>
            <ContainedButton type="submit" disabled={!commentClipRangeValid}>Continue</ContainedButton>
          </div>
        </form>
      </Modal>}
      {showCommentComposerPicker && <SendClipModal open skipConfirmation
        switchModal={(open) => setShowCommentComposerPicker(!!open)}
        sendToComposer={(destinationId) => sendCommentClip({ destinationId })}
        onCreateComposer={() => { setShowCommentComposerPicker(false); setShowNewCommentComposer(true); }} />}
      {showNewCommentComposer && <NewComposerModal open skipConfirmation
        switchModal={() => setShowNewCommentComposer(false)}
        createComposer={(title) => sendCommentClip({ title })} />}
    </div>
  );
};

export default VideoComments;
